import React from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  StatusBar,
  RefreshControl,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { GradientBorderFrame } from '@/components/common/admin/mobile/GradientBorderFrame';

/**
 * Default dark theme colors for mobile screens
 * Individual screens can override with their own color constants
 */
const DEFAULT_COLORS = {
  background: '#171717',
};

interface MobileScreenLayoutProps {
  /** Child content to render inside the layout */
  children: React.ReactNode;
  /** Background color for the frames (default: #171717) */
  backgroundColor?: string;
  /** Whether to wrap content in a ScrollView (default: true) */
  scrollable?: boolean;
  /** Gap between content items (default: 16) */
  contentGap?: number;
  /** Padding for scroll content (default: 20) */
  contentPadding?: number;
  /** Bottom padding for scroll content (default: 40) */
  contentPaddingBottom?: number;
  /** Pull-to-refresh handler */
  onRefresh?: () => void;
  /** Whether refresh is in progress */
  refreshing?: boolean;
  /** Refresh control tint color */
  refreshTintColor?: string;
  /** Show vertical scroll indicator (default: false) */
  showScrollIndicator?: boolean;
  /** Custom scroll view style */
  scrollViewStyle?: StyleProp<ViewStyle>;
  /** Custom content container style (overrides default padding/gap) */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Whether to show status bar as light content (default: true) */
  lightStatusBar?: boolean;
}

/**
 * MobileScreenLayout
 * 
 * Reusable layout component for mobile screens with the new Figma design.
 * Provides the nested gradient border frame pattern used across all mobile screens.
 * 
 * Features:
 * - Outer gradient frame (3px border, 32px radius)
 * - Inner gradient frame (1px border, 27px radius)
 * - Optional ScrollView with pull-to-refresh
 * - Configurable spacing and colors
 * 
 * @example
 * // Basic usage with ScrollView
 * <MobileScreenLayout>
 *   <MyContent />
 * </MobileScreenLayout>
 * 
 * @example
 * // With pull-to-refresh
 * <MobileScreenLayout
 *   onRefresh={handleRefresh}
 *   refreshing={isRefreshing}
 * >
 *   <MyContent />
 * </MobileScreenLayout>
 * 
 * @example
 * // Non-scrollable content (e.g., with custom tabs)
 * <MobileScreenLayout scrollable={false}>
 *   <TabBar />
 *   <TabContent />
 * </MobileScreenLayout>
 */
export const MobileScreenLayout: React.FC<MobileScreenLayoutProps> = ({
  children,
  backgroundColor = DEFAULT_COLORS.background,
  scrollable = true,
  contentGap = 16,
  contentPadding = 20,
  contentPaddingBottom = 40,
  onRefresh,
  refreshing = false,
  refreshTintColor = '#EEF1F4',
  showScrollIndicator = false,
  scrollViewStyle,
  contentContainerStyle,
  lightStatusBar = true,
}) => {
  const insets = useSafeAreaInsets();
  const frameStyle = { backgroundColor };
  
  const defaultContentStyle = {
    flexGrow: 1,
    padding: contentPadding,
    paddingBottom: contentPaddingBottom + insets.bottom,
    gap: contentGap,
  };

  const renderContent = () => {
    if (!scrollable) {
      return children;
    }

    return (
      <ScrollView
        style={[styles.scrollView, scrollViewStyle]}
        contentContainerStyle={[defaultContentStyle, contentContainerStyle]}
        showsVerticalScrollIndicator={showScrollIndicator}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={refreshTintColor}
            />
          ) : undefined
        }
      >
        {children}
      </ScrollView>
    );
  };

  return (
    <>
      {lightStatusBar && <StatusBar barStyle="light-content" />}
      
      {/* Outer gradient frame - 3px border, 32px radius */}
      <GradientBorderFrame
        borderWidth={3}
        borderRadius={32}
        innerStyle={frameStyle}
      >
        <View style={styles.innerFrameSpacing}>
          {/* Inner gradient frame - 1px border, 27px radius */}
          <GradientBorderFrame
            borderWidth={1}
            borderRadius={27}
            innerStyle={{ ...styles.innerFrameContent, ...frameStyle }}
          >
            {renderContent()}
          </GradientBorderFrame>
        </View>
      </GradientBorderFrame>
    </>
  );
};

const styles = StyleSheet.create({
  innerFrameSpacing: {
    flex: 1,
    paddingTop: 2,
    paddingHorizontal: 2,
  },
  innerFrameContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
});

export default MobileScreenLayout;
